import { Badge } from '@neo/index'
import { cn } from '@neo/lib/utils'
import { Button } from '@neo/primitives/button'
import { ArrowUpDown, PhoneOff, UserRoundPlus } from 'lucide-react'
import { ElapsedTimer } from 'v2/shared/components/elapsed-timer'
import { usePhoneStore } from 'v2/state/phone'
import { ConvChannels } from 'v2/types/api/conversations'
import { type Participant, ParticipantState } from 'v2/types/call'
import { useTransferMutations } from '../../mutations/conversations'
import { getFormattedContactName } from '../../utils'

interface ParticipantSectionProps {
  participant: Participant
}

export const ParticipantSection = ({ participant }: ParticipantSectionProps) => {
  const { toggleCall, hangupParticipant, cancelTransfer } = useTransferMutations()
  const activeCall = usePhoneStore((state) => state.activeCall)
  const callConversation = usePhoneStore((state) => state.callConversation)
  const isConnected = participant.state === ParticipantState.CONNECTED

  const handleSwitch = () => {
    toggleCall.mutate(
      {
        contact_uuid: callConversation?.callChildConversation?.contact_uuid,
      },
      {
        onSuccess: () => {
          usePhoneStore.getState().setOnHold(!!participant.isOnHold)
          usePhoneStore.getState().updateParticipant(participant.member, {
            isOnHold: !participant.isOnHold,
          })
        },
      },
    )
  }

  if (!activeCall?.callUUID) return null

  return (
    <div className="flex flex-col py-2 border-t border-border mt-4">
      <div className="text-xs-400 text-secondary mb-2 mt-1 flex items-center gap-1">
        <UserRoundPlus className="size-4 text-status-success" />
        Invited participant
      </div>
      <div className="flex items-center justify-between ml-5">
        <div className="flex flex-col w-full">
          <span className="text-p-600 flex items-center justify-between w-full">
            {getFormattedContactName(participant.identifier)}
            <Badge variant="warning" className={cn(!participant.isOnHold && 'invisible')}>
              On Hold
            </Badge>
          </span>
          <span className="text-p-400 text-tertiary">
            {!isConnected ? (
              'Ringing...'
            ) : (
              <span>
                {participant.startAt && (
                  <ElapsedTimer
                    startTime={participant.startAt}
                    format="mm:ss"
                    className="text-p-400 text-tertiary"
                  />
                )}
              </span>
            )}
          </span>
        </div>
        {!isConnected && (
          <div className="flex items-center gap-2 ml-5 mt-2">
            <Button
              variant="destructive-outline"
              size="xs"
              disabled={cancelTransfer.isPending}
              onClick={() =>
                cancelTransfer.mutate({
                  channel: ConvChannels.CALL,
                  transferType: participant.state === ParticipantState.CONNECTING ? 'warm' : 'cold',
                  contactUuid: callConversation?.callChildConversation?.contact_uuid ?? '',
                  member: participant.member,
                })
              }
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      {isConnected && (
        <div className="flex items-center gap-2 ml-5 mt-2">
          <Button
            tooltipContent="Switch to customer"
            variant="tertiary"
            disabled={toggleCall.isPending || participant.isOnHold}
            disabledReason={
              participant.isOnHold
                ? 'Cannot switch - already talking to customer'
                : toggleCall.isPending
                  ? 'Switching call...'
                  : ''
            }
            size="iconSm"
            onClick={handleSwitch}
            data-testid="button-switch-call"
            className={cn('rounded-full')}
          >
            <ArrowUpDown className="size-4" />
          </Button>

          <Button
            variant="destructive-outline"
            size="iconSm"
            tooltipContent="Hang up this participant"
            onClick={() =>
              hangupParticipant.mutate({
                contact_uuid: callConversation?.callChildConversation?.contact_uuid,
              })
            }
            data-testid="button-hangup-call"
            className="rounded-full"
          >
            <PhoneOff className="size-4 fill-destructive" />
          </Button>
        </div>
      )}
    </div>
  )
}
